import { ReactNode } from 'react'
import { PiX } from 'react-icons/pi'
import FbButton, { FbButtonVariants } from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { cn } from 'components/UI/cn'

type IconComponent = React.ComponentType<{ size?: number; className?: string }>

export interface NoticeModalAction {
  label: string
  /** DS2026 button variant. Defaults to `secondary`. */
  variant?: FbButtonVariants
  /** Optional leading icon. */
  icon?: IconComponent
  onClick?: () => void
  loading?: boolean
}

export interface NoticeModalProps {
  /** Icon shown inside the badge. */
  icon: IconComponent
  /** Badge background classes. Default: `bg-brand-sage-50`. */
  badgeClassName?: string
  /** Icon color classes. Default: `text-brand-active`. */
  iconClassName?: string
  title: string
  description: ReactNode
  /** Buttons, rendered stacked (first one on top). */
  actions?: NoticeModalAction[]
  /** When provided, renders the floating close (×) button. */
  onClose?: () => void
  className?: string
}

/**
 * NoticeModal — DS2026 centered notice / empty-state modal.
 *
 * Mirrors the DS2026 "Info modal" (Figma node 8208:1152): a centered notice with
 * an icon badge → title → subtitle → stacked action buttons, and a floating
 * close (×) only — no title-row header. Buttons render through
 * `Ds2026ButtonProvider`, so `variant="primary"` is the Basil-600 fill and
 * `variant="secondary"` the white/Gray-300 outline. Sibling to the DS2026
 * `Alert` (inline) and `NoticeBanner` (elevated bar/card) surfaces.
 *
 * Presentational only: drop it inside the app `Modal`/`Dialog`
 * (`components/UI/Modal`) for the overlay + z-index when wiring it up for real.
 */
export function NoticeModal({
  icon: Icon,
  badgeClassName = 'bg-brand-sage-50',
  iconClassName = 'text-brand-active',
  title,
  description,
  actions,
  onClose,
  className,
}: NoticeModalProps) {
  const hasActions = actions != null && actions.length > 0

  return (
    <div
      className={cn(
        'relative mx-auto flex w-[360px] flex-col items-center gap-6 rounded-2xl bg-white px-4 pb-4 pt-5 shadow-xl',
        className
      )}
    >
      {onClose && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1.5 text-fg-tertiary transition-colors hover:bg-surface-sunken hover:text-fg-secondary"
        >
          <PiX className="size-5" />
        </button>
      )}

      {/* Icon badge (approximates the DS starburst badge) */}
      <div
        className={cn(
          'flex size-[70px] items-center justify-center rounded-2xl',
          badgeClassName
        )}
      >
        <Icon size={32} className={iconClassName} />
      </div>

      {/* Title + subtitle */}
      <div className="flex flex-col items-center gap-4 text-center tracking-[-0.2px]">
        <h2 className="text-2xl font-semibold leading-8 text-fg">{title}</h2>
        <p className="text-base font-normal leading-6 text-fg-secondary">
          {description}
        </p>
      </div>

      {/* Stacked buttons — first action on top */}
      {hasActions && (
        <Ds2026ButtonProvider>
          <div className="flex w-full flex-col gap-3">
            {actions.map((action, index) => {
              const ActionIcon = action.icon
              return (
                <FbButton
                  key={index}
                  variant={action.variant ?? 'secondary'}
                  loading={action.loading}
                  onClick={action.onClick}
                  className="w-full justify-center"
                >
                  {ActionIcon && <ActionIcon size={16} />}
                  <span>{action.label}</span>
                </FbButton>
              )
            })}
          </div>
        </Ds2026ButtonProvider>
      )}
    </div>
  )
}
